// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@cartero/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  /** Optional call-to-action (e.g. an "Add task" button) rendered below the copy. */
  action?: JSX.Element;
  /**
   * When provided, the illustrative icon becomes an interactive target that
   * triggers the same action as the primary CTA (open a dialog, navigate, or
   * focus an input) so the empty state never feels like a dead end. Tapping the
   * icon is a discoverable shortcut to the CTA — leave it undefined for purely
   * informational empty states (the icon stays decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive icon (required when `onIconActivate`
   * is set). Should describe the action, e.g. "Add your first task".
   */
  iconActionLabel?: string;
  class?: string;
}

/**
 * Premium empty state: a brand-tinted, layered illustration with a gentle
 * float, clear hierarchy, and an optional CTA. Keeps the calm, focused Cartero
 * identity (warm postal-stamp glow over a soft ring). The decorative SVG orbit is
 * theme-aware (uses `currentColor` inherited from the primary token) and purely
 * presentational (`aria-hidden`). All motion is suppressed under
 * `prefers-reduced-motion` via the global guard.
 */
export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    <div class="animate-scale-in">
      <div class="relative mb-6">
        {/* Soft brand glow behind the mark */}
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 -z-10 rounded-full bg-primary/15 blur-2xl"
        />

        {/* Decorative orbit ring — theme-aware via currentColor (primary). */}
        <svg
          aria-hidden="true"
          viewBox="0 0 120 120"
          class="pointer-events-none absolute -inset-4 -z-[5] h-[calc(100%+2rem)] w-[calc(100%+2rem)] text-primary/40"
        >
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-dasharray="3 8"
            stroke-linecap="round"
            class="origin-center animate-[spin_24s_linear_infinite] opacity-50"
          />
          <circle cx="60" cy="6" r="3" fill="currentColor" class="opacity-70" />
          <circle cx="108" cy="78" r="2" fill="currentColor" class="opacity-50" />
          <circle cx="14" cy="84" r="2.5" fill="currentColor" class="opacity-40" />
        </svg>

        {/* When an action is wired, the icon becomes a real button so tap/click/
            keyboard (Enter/Space, native to <button>) triggers the CTA. Otherwise
            it stays a decorative div. Focus ring is visible; the float animation
            is already suppressed under the global prefers-reduced-motion guard. */}
        <Show
          when={props.onIconActivate}
          fallback={
            <div class="rounded-full bg-gradient-to-br from-primary/10 to-primary/5 p-6 ring-1 ring-primary/15 animate-float">
              <props.icon class="h-12 w-12 text-primary/70" />
            </div>
          }
        >
          {(activate) => (
            <button
              type="button"
              aria-label={props.iconActionLabel ?? props.title}
              onClick={() => {
                activate()();
              }}
              class="group rounded-full bg-gradient-to-br from-primary/10 to-primary/5 p-6 ring-1 ring-primary/15 animate-float transition-[transform,box-shadow,background-color] duration-200 hover:from-primary/20 hover:to-primary/10 hover:ring-primary/30 hover:shadow-lg hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
            >
              <props.icon class="h-12 w-12 text-primary/70 transition-colors group-hover:text-primary" />
            </button>
          )}
        </Show>
      </div>
    </div>
    <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 max-w-xs text-sm text-muted-foreground text-balance">{props.description}</p>
    </Show>
    <Show when={props.action}>
      <div class="mt-6">{props.action}</div>
    </Show>
  </div>
);
